import { useRouter } from 'next/router'
import { useCallback, useRef } from 'react'
import { graphqlFetch, GraphqlFetchOutput } from '@pylot-data/graphqlFetch'
import type { Cart } from '@pylot-data/pylotschema'
import { removeItemFromCartMutation } from './graphql/removeItemFromCartMutation'
import { deleteAllProductsFromCartMutation } from './graphql/deleteAllProductsFromCartMutation'
import { CartData, useCart } from './use-cart'
import { waitUntilCartIsEmpty } from './utils/waitUntilCartIsEmpty'
import { getIsSignedIn } from './utils/getIsSignedIn'
import { getGrandTotal } from './utils/optimisticAddToCart/getGrandTotal'
import { useUI } from '@corratech/pylot-ui/context'

export type RemoveItemFromCartInput = {
    input: {
        cart_id: string
        cart_item_uid: string
    }
    isSignedIn: boolean
    displayLoadingIndicator?: boolean
}

export type DeleteAllProductsFromCartInput = {
    cart_id: string
}

export type DeleteAllProductsFromCartOutput = {
    deleteAllproductsFromCart: {
        status: boolean
    }
}

export type RemoveItemFromCartOutput = {
    removeItemFromCart: {
        cart: Cart
    }
}

export type RemoveItemFromCart = (
    cartItemUid: string,
    shouldDoOptimisticUpdate?: boolean,
    displayLoadingIndicator?: boolean
) => Promise<void>

export type RemoveAllItemsFromCart = () => Promise<void>

export interface UseRemoveItemFromCartReturn {
    removeItemFromCart: RemoveItemFromCart
    removeAllItemsFromCart: RemoveAllItemsFromCart
}

const formatData = (
    output: GraphqlFetchOutput<RemoveItemFromCartOutput>
): CartData | null => {
    const cart = output?.data?.removeItemFromCart?.cart
    if (!cart) return null
    return { data: { cart } }
}

export const getOptimisticRemoveItemData = (
    cartData: CartData,
    cartItemUid: string
): CartData => {
    const items = [...(cartData.data.cart.items || [])]
    const itemIndex = items.findIndex((item) => item?.uid === cartItemUid)

    if (itemIndex === -1) {
        return cartData
    }

    items.splice(itemIndex, 1)

    const updatedGrandTotal = getGrandTotal(
        items,
        cartData.data.cart.prices?.grand_total
    )

    return {
        data: {
            cart: {
                ...cartData.data.cart,
                items,
                prices: {
                    ...cartData.data.cart.prices,
                    grand_total: updatedGrandTotal,
                    subtotal_excluding_tax: updatedGrandTotal
                }
            },
            isOptimistic: true
        }
    }
}

const getOptimisticDataRemoveAllItems = (cartData: CartData): CartData => ({
    data: {
        cart: {
            ...cartData.data.cart,
            items: [],
            prices: {
                ...cartData.data.cart.prices,
                grand_total: {
                    ...cartData.data.cart.prices?.grand_total,
                    value: 0
                },
                subtotal_excluding_tax: {
                    ...cartData.data.cart.prices?.subtotal_excluding_tax,
                    value: 0
                }
            }
        },
        isOptimistic: true
    }
})

export const useRemoveItemFromCart = (): UseRemoveItemFromCartReturn => {
    const { locale } = useRouter()
    const { data, mutate } = useCart()
    const { setMinicartLoading } = useUI()

    const pendingRequestsRef = useRef(0)

    const startLoading = useCallback(
        (enabled: boolean) => {
            if (!enabled) return
            pendingRequestsRef.current += 1
            setMinicartLoading(true)
        },
        [setMinicartLoading]
    )

    const stopLoading = useCallback(
        (enabled: boolean) => {
            if (!enabled) return
            pendingRequestsRef.current = Math.max(
                0,
                pendingRequestsRef.current - 1
            )
            if (pendingRequestsRef.current === 0) {
                setMinicartLoading(false)
            }
        },
        [setMinicartLoading]
    )

    return {
        removeItemFromCart: useCallback(
            async (
                cartItemUid: string,
                shouldDoOptimisticUpdate = true,
                displayLoadingIndicator = false
            ) => {
                const cartId = data?.data?.cart?.id
                if (!cartId) return

                startLoading(displayLoadingIndicator)

                try {
                    if (shouldDoOptimisticUpdate) {
                        await mutate((currentCartData?: CartData) => {
                            if (!currentCartData) return currentCartData
                            return getOptimisticRemoveItemData(
                                currentCartData,
                                cartItemUid
                            )
                        }, false)
                    }

                    await graphqlFetch<
                        RemoveItemFromCartInput,
                        RemoveItemFromCartOutput
                    >({
                        query: removeItemFromCartMutation,
                        variables: {
                            input: {
                                cart_id: String(cartId),
                                cart_item_uid: cartItemUid
                            },
                            isSignedIn: getIsSignedIn()
                        },
                        locale
                    })

                    // Revalidate from server after mutation completes.
                    // This is safer than applying potentially stale full-cart snapshots
                    // from overlapping responses.
                    await mutate()
                } finally {
                    stopLoading(displayLoadingIndicator)
                }
            },
            [data?.data?.cart?.id, locale, mutate, startLoading, stopLoading]
        ),

        removeAllItemsFromCart: useCallback(async () => {
            const cartId = data?.data?.cart?.id
            if (!cartId) {
                throw new Error('No hay un carrito para vaciar')
            }

            await mutate((currentCartData?: CartData) => {
                if (!currentCartData) return currentCartData
                return getOptimisticDataRemoveAllItems(currentCartData)
            }, false)

            const response = await graphqlFetch<
                DeleteAllProductsFromCartInput,
                DeleteAllProductsFromCartOutput
            >({
                query: deleteAllProductsFromCartMutation,
                variables: {
                    cart_id: String(cartId)
                },
                customPylotQueryHeader: 'deleteAll',
                locale
            })

            if (response?.errors?.length) {
                await mutate()
                throw new Error(
                    response.errors[0]?.message ||
                        'No se pudo vaciar el carrito'
                )
            }

            if (response?.data?.deleteAllproductsFromCart?.status === false) {
                await mutate()
                throw new Error('No se pudo vaciar el carrito')
            }

            const emptied = await waitUntilCartIsEmpty(() => mutate())
            if (!emptied) {
                throw new Error('El carrito todavía tiene productos')
            }
        }, [data?.data?.cart?.id, locale, mutate])
    }
}
